<template>
  <div class="row">
    <language-selector />

    <div class="card-wrapper">
      <card>
        <div class="card card-nav-tabs card-plain mt-2">
          <div class="nav-tabs-navigation">
            <div class="nav-tabs-wrapper">
              <draggable
                tag="ul"
                class="nav nav-tabs"
                v-model="categories"
                v-bind="dragOptionsTabItems"
              >
                <li v-for="tab in categories" :key="tab.id" class="nav-item">
                  <button
                    class="nav-link btn-tab"
                    :class="{ active: selectedTabId === tab.id }"
                    @click="setSelectedTabId(tab.id)"
                  >
                    {{
                      activeLanguage
                        ? tab.label[activeLanguage.code]
                        : tab.label
                    }}
                    <fa
                      class="tab-icon"
                      :icon="['fas', 'grip-lines-vertical']"
                    />
                  </button>
                </li>
                <li class="nav-item">
                  <button
                    slot="footer"
                    class="nav-link btn-tab"
                    @click="addTabItem"
                  >
                    +
                  </button>
                </li>
              </draggable>
            </div>
          </div>

          <div class="tab-content">
            <div
              v-for="category in categories"
              :key="category.id"
              class="tab-pane"
              :class="{ active: selectedTabId === category.id }"
            >
              <h4 class="card-title mt-5 mb-4">Edit Category Details</h4>
              <div>
                <div class="form-group">
                  <label for="heading">
                    Heading
                    <localised-field-indicator />
                  </label>
                  <input
                    v-model="category.label[activeLanguage.code]"
                    class="form-control form-control-lg"
                    type="text"
                    :dir="activeLanguage.rtl ? 'rtl' : false"
                    id="heading"
                  />
                </div>

                <div class="form-group">
                  <label for="defaultChildId">Default Child</label>
                  <p>
                    <small
                      >Set the default child page to open when first navigating
                      to category</small
                    >
                  </p>
                  <select
                    class="form-control"
                    v-model="category.defaultChildUrl"
                  >
                    <option value="" selected>Select Page</option>
                    <option
                      v-for="baseItem in category.children"
                      :value="
                        baseItem.pageId
                          ? `/category/${selectedTabId}/page/${baseItem.pageId}`
                          : baseItem.customRoute
                      "
                      :key="baseItem._id"
                    >
                      <span v-html="baseItem.label[activeLanguage.code]"></span>
                    </option>
                  </select>
                </div>

                <div class="form-group">
                  <label>Custom Route</label>
                  <toggle-button
                    v-model="category.isCustomRoute"
                    color="#28a745"
                    :sync="true"
                    :labels="false"
                    :width="50"
                    :height="30"
                  />
                </div>

                <div v-if="category.isCustomRoute" class="form-group">
                  <label for="customRoute">Custom Route Slug</label>
                  <p>
                    <small
                      >Enter the slug you want this page to redirect to</small
                    >
                  </p>
                  <input
                    v-model="category.customRoute"
                    class="form-control form-control-lg"
                    type="text"
                    name="customRoute"
                    id="customRoute"
                    placeholder="/living-options"
                  />
                </div>
              </div>

              <h4 class="card-title my-4">Edit Category Navigation</h4>

              <nav-items-draggable
                :category="category"
                :navigation="navigation.categories"
                :selectedItemId="selectedNavItem.id"
                :selectedTabId="selectedTabId"
                @addBaseNavItem="addBaseNavItem"
                @addChildNavItem="addChildNavItem"
                @navItemClick="navItemClick"
                @setBaseItemsOrder="setBaseItemsOrder"
                @setChildItemsOrder="setChildItemsOrder"
              />

              <div class="d-flex">
                <button
                  class="btn btn-outline-danger mt-4 ml-auto"
                  @click="removeCategory"
                >
                  Remove Category
                </button>
              </div>
            </div>
            <!-- screen loop -->
          </div>

          <modal-edit-box
            :isVisible="this.selectedNavItem.label"
            :isArrowVisible="true"
            :closeEditBox="closeEditBox"
            :xPos="this.modalX"
            :yPos="this.modalY"
            class="modal-edit-box"
            @remove="removeSelectedItem"
            @update="closeEditBox"
          >
            <div slot="input" class="form-group form-group--sm">
              <label for="modal-label">
                Label
                <localised-field-indicator />
              </label>
              <input
                v-if="selectedNavItem.label"
                type="text"
                class="form-control form-control-lg"
                placeholder="Label"
                v-model="selectedNavItem.label[activeLanguage.code]"
                id="modal-label"
              />
            </div>

            <div
              slot="input"
              class="form-group form-group--sm"
              v-if="selectedNavItem.icon"
            >
              <label>Icon Style</label>
              <select v-model="selectedNavItem.icon[0]" class="form-control">
                <option value="fas">Solid</option>
                <option value="fal">Light</option>
                <option value="fad">Duotone</option>
              </select>
              <label>Icon Code</label>
              <input
                type="text"
                class="form-control form-control-lg"
                placeholder="Set Icon Code"
                v-model="selectedNavItem.icon[1]"
              />
              <div v-if="selectedNavItem.icon" class="preview">
                <fa
                  v-if="
                    selectedNavItem.icon[0] !== undefined &&
                      selectedNavItem.icon[0] !== '' &&
                      selectedNavItem.icon[1] !== ''
                  "
                  :icon="[selectedNavItem.icon[0], selectedNavItem.icon[1]]"
                />
              </div>
            </div>

            <div slot="input" class="form-group form-group--sm">
              <label for="modal-trigger">Video Wall Trigger</label>
              <input
                type="text"
                class="form-control form-control-lg"
                placeholder="Video Wall Trigger"
                v-model="selectedNavItem.videoWallTrigger"
                id="modal-trigger"
              />
            </div>

            <div slot="input" class="form-group form-group--sm">
              <label>Select Page</label>
              <select class="form-control" v-model="selectedNavItem.pageId">
                <option :value="undefined" selected>Select Page</option>
                <option
                  v-for="page in pagesOrdered"
                  :key="page._id"
                  :value="page._id"
                >
                  <span>{{ getPageName(page) }}</span>
                </option>
              </select>
            </div>

            <div slot="input" class="form-group form-group--sm">
              <label>External URL</label>
              <textarea
                type="text"
                class="form-control form-control-lg"
                placeholder="/category/<id>/page/<id>"
                v-model="selectedNavItem.externalUrl"
                id="modal-trigger"
              />
            </div>

            <div slot="input" class="form-group form-group--sm">
              <label>Is Product Route</label>
              <toggle-button
                :value="isSelectedProductRoute"
                @change="setSelectedProductRouteEnabled($event.value)"
                :color="{ checked: '#28a745', unchecked: '#b8b8b8' }"
                :sync="true"
                :width="30"
                :height="20"
              />
            </div>

            <div
              v-if="isSelectedProductRoute"
              slot="input"
              class="form-group form-group--sm"
            >
              <label>Mode</label>
              <select
                class="form-control"
                :value="selectedProductRouteMode"
                @change="setSelectedProductRouteMode($event.target.value)"
              >
                <option value="masterplan">Masterplan</option>
                <option value="carousel">Carousel</option>
                <option value="displayhomes">Display Homes</option>
              </select>
            </div>

            <div
              v-if="
                isSelectedProductRoute &&
                  selectedProductRouteMode !== 'displayhomes'
              "
              slot="input"
              class="form-group form-group--sm"
            >
              <label>Type</label>
              <select
                class="form-control"
                :value="selectedProductRouteType"
                @change="setSelectedProductRouteType($event.target.value)"
              >
                <option
                  v-for="typeOption in productRouteTypeOptions"
                  :key="typeOption.value"
                  :value="typeOption.value"
                >
                  {{ typeOption.label }}
                </option>
              </select>
            </div>

            <div
              v-if="
                isSelectedProductRoute &&
                  selectedProductRouteMode !== 'displayhomes' &&
                  selectedProductRouteType === 'other'
              "
              slot="input"
              class="form-group form-group--sm"
            >
              <label>Specify "Other" Type</label>
              <input
                type="text"
                class="form-control form-control-lg"
                placeholder="e.g. villas, townhomes..."
                :value="selectedProductRouteCustomType"
                @input="setSelectedProductRouteCustomType($event.target.value)"
              />
            </div>

            <div
              v-if="selectedNavItem.children"
              slot="input"
              class="form-group form-group--sm"
            >
              <label>Is Live Section</label>
              <toggle-button
                v-model="selectedNavItem.isLiveSection"
                :color="{ checked: '#28a745', unchecked: '#b8b8b8' }"
                :sync="true"
                :width="30"
                :height="20"
              />
            </div>

            <div slot="input" class="form-group form-group--sm">
              <label>Custom Route</label>
              <textarea
                type="text"
                class="form-control form-control-lg"
                placeholder="/category/<id>/product/masterplan"
                v-model="selectedNavItem.customRoute"
                @input="
                  syncSelectedProductRouteFromCustomRoute($event.target.value)
                "
                id="modal-custom-route"
              />
            </div>
          </modal-edit-box>
        </div>
      </card>
    </div>

    <div class="col-12">
      <button class="btn btn-primary" @click="updateNavigation">Save</button>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import {
  FETCH_NAVIGATION,
  FETCH_PAGES,
  NAVIGATION_EDIT
} from "@/store/actions.type";
import LanguageSelector from "@/components/LanguageSelector";
import NavItemsDraggable from "@/components/NavItemsDraggable";
import NavigationPreview from "@/components/NavigationPreview";
import draggable from "vuedraggable";
import _ from "lodash";
import Swal from "sweetalert2";

export default {
  components: {
    draggable,
    LanguageSelector,
    NavigationPreview,
    NavItemsDraggable
  },

  data() {
    return {
      initialPageJSON: null,
      modalWidth: 125,
      modalX: 0,
      modalY: 0,
      selectedNavItem: {},
      selectedTabId: null,
      productRouteTypes: ["any", "land", "packages", "dwellings", "turnkeys"],
      productRouteTypesByMode: {
        masterplan: [
          { value: "any", label: "Any" },
          { value: "land", label: "Land" },
          { value: "turnkeys", label: "Turnkeys" },
          { value: "other", label: "Other" }
        ],
        carousel: [
          { value: "any", label: "Any" },
          { value: "land", label: "Land" },
          { value: "packages", label: "Packages" },
          { value: "dwellings", label: "Dwellings" },
          { value: "turnkeys", label: "Turnkeys" },
          { value: "other", label: "Other" }
        ]
      },
      productRouteForm: {
        mode: "masterplan",
        type: "any",
        customType: ""
      },
      productRoutePreviousCustomRoute: null
    };
  },

  computed: {
    ...mapGetters(["pages", "navigation", "activeLanguage"]),

    currentPageJSON() {
      return JSON.stringify(this.navigation);
    },

    isPageChanged() {
      return this.currentPageJSON !== this.initialPageJSON;
    },

    categories: {
      get: function() {
        if (this.navigation) {
          return this.navigation.categories;
        } else {
          return null;
        }
      },

      set: function(newVal) {
        this.navigation.categories = newVal;
      }
    },

    dragOptionsTabItems() {
      return {
        animation: 120,
        group: "tabs",
        disabled: false,
        ghostClass: "ghostly"
      };
    },

    highestNavId() {
      let ids = [];

      if (this.navigation.categories) {
        this.navigation.categories.forEach(screen => {
          if (!isNaN(screen.id)) {
            ids.push(screen.id);
          }

          screen.children.forEach(baseItem => {
            if (!isNaN(baseItem.id)) {
              ids.push(baseItem.id);
            }

            baseItem.children.forEach(childItem => {
              if (!isNaN(childItem.id)) {
                ids.push(childItem.id);
              }
            });
          });
        });
      }

      let highestId = Math.max(...ids) + 1;

      if (highestId == "-Infinity") {
        highestId = 1;
      }

      return highestId.toString();
    },

    pagesOrdered() {
      let pagesOrdered = _.orderBy(this.pages, [
        `heading.${this.activeLanguage.code}`
      ]);
      return pagesOrdered;
    },

    selectedCategory() {
      if (this.navigation) {
        let category = _.find(this.navigation.categories, category => {
          return category.id == this.selectedTabId;
        });
        return category;
      } else {
        return null;
      }
    },

    selectedProductRoute() {
      return this.getProductRoute(this.selectedNavItem.customRoute);
    },

    isSelectedProductRoute() {
      return this.selectedProductRoute.isProductRoute;
    },

    selectedProductRouteMode() {
      return this.productRouteForm.mode;
    },

    selectedProductRouteType() {
      return this.productRouteForm.type;
    },

    selectedProductRouteCustomType() {
      return this.productRouteForm.customType;
    },

    productRouteTypeOptions() {
      return this.productRouteTypesByMode[this.selectedProductRouteMode] || [];
    }
  },

  beforeMount() {
    this.$store
      .dispatch(FETCH_NAVIGATION)
      .then(res => {
        this.initialPageJSON = JSON.stringify(this.navigation);

        if (res !== null && !_.isEmpty(this.navigation.categories)) {
          this.selectedTabId = this.navigation.categories[0].id;
        } else {
          this.$notify({
            message: "No navigation items found",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "warning"
          });
        }
      })
      .catch(err => {
        this.$notify({
          message: "Error - " + err,
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "danger"
        });
      });
  },

  beforeRouteLeave(to, from, next) {
    if (this.isPageChanged) {
      if (
        window.confirm("Warning! Unsaved changes. Do you really want to leave?")
      ) {
        next();
      }
    } else {
      next();
    }
  },

  mounted() {
    this.$store.dispatch(FETCH_PAGES);
  },

  methods: {
    addTabItem() {
      let newTab = {
        id: this.highestNavId,
        label: {
          en: "New Tab"
        },
        order: "",
        slug: "",
        defaultChildId: null,
        children: []
      };

      this.navigation.categories.push(newTab);
      this.selectedTabId = newTab.id;
    },

    addChildNavItem(event) {
      // get parent id
      let parentEl = event.target.closest(".nav-item");
      let childWrapper = parentEl.querySelector(".nav-item-child");
      let parentId = parentEl.getAttribute("id");

      // get parent element
      let parentItem = _.find(this.selectedCategory.children, baseItem => {
        return baseItem.id == parentId;
      });

      // add child element
      let child = {
        id: this.highestNavId,
        label: {
          en: "New Nav Item"
        },
        order: childWrapper.children.length,
        pageId: null,
        videoWallTrigger: null,
        icon: ["", ""],
        customRoute: "",
        externalUrl: ""
      };

      // attach child item to parent
      parentItem.children.push(child);
      this.closeEditBox();
    },

    addBaseNavItem() {
      let length = this.selectedCategory.children.length;
      const maxBaseItems = 7;

      if (length >= maxBaseItems) {
        this.$notify({
          message: `Can\'t exceed ${maxBaseItems} base items`,
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "warning"
        });
      } else {
        let newItem = {
          id: this.highestNavId,
          label: {
            en: "New Nav Item"
          },
          order: length,
          pageId: null,
          videoWallTrigger: null,
          customRoute: "",
          externalUrl: "",
          children: []
        };

        this.selectedCategory.children.push(newItem);
        this.closeEditBox();
      }
    },

    closeEditBox() {
      this.selectedNavItem = {};
      this.productRoutePreviousCustomRoute = null;
    },

    getPageName(page) {
      if (page.name) {
        return page.name;
      }

      if (this.activeLanguage) {
        return page.heading[this.activeLanguage.code];
      }

      return page.heading;
    },

    buildProductRoute(mode = "masterplan", type = "any", customType = "") {
      if (mode === "displayhomes") {
        return `/category/${this.selectedTabId}/product/displayhomes`;
      }

      const routeMode = mode === "carousel" ? "carousel" : "masterplan";
      const typeSegment =
        type === "other" ? this.getRouteSegment(customType) : type;
      const baseRoute = `/category/${this.selectedTabId}/product/${routeMode}`;

      if (!typeSegment || typeSegment === "any") {
        return baseRoute;
      }

      return `${baseRoute}/${typeSegment}`;
    },

    getProductRoute(route = "") {
      const defaultRoute = {
        isProductRoute: false,
        mode: "masterplan",
        type: "any",
        customType: ""
      };

      if (!route) {
        return defaultRoute;
      }

      if (/^\/category\/[^/]+\/product\/displayhomes(?:\/|$)/.test(route)) {
        return {
          isProductRoute: true,
          mode: "displayhomes",
          type: "any",
          customType: ""
        };
      }

      const match = route.match(
        /^\/category\/[^/]+\/product\/(masterplan|carousel)(?:\/([^/?#]+))?/
      );

      if (!match) {
        return defaultRoute;
      }

      const routeType = this.getDecodedRouteSegment(match[2]);
      let type = "any";

      if (this.productRouteTypes.indexOf(routeType) !== -1) {
        type = routeType;
      } else if (routeType) {
        type = "other";
      }

      return {
        isProductRoute: true,
        mode: match[1],
        type,
        customType: type === "other" ? routeType : ""
      };
    },

    getRouteSegment(value = "") {
      const segment = value
        .toString()
        .trim()
        .replace(/^\/+|\/+$/g, "")
        .replace(/[/?#]+/g, "-")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .toLowerCase();

      return encodeURIComponent(segment);
    },

    getDecodedRouteSegment(value = "") {
      if (!value) {
        return "";
      }

      try {
        return decodeURIComponent(value);
      } catch (e) {
        return value;
      }
    },

    setProductRouteForm(productRoute) {
      this.productRouteForm = {
        mode: productRoute.mode || "masterplan",
        type: productRoute.type || "any",
        customType: productRoute.customType || ""
      };
    },

    syncSelectedProductRouteFromCustomRoute(route = "") {
      const productRoute = this.getProductRoute(route);

      if (productRoute.isProductRoute) {
        this.setProductRouteForm(productRoute);
      }
    },

    setSelectedCustomRoute(route) {
      if (
        Object.prototype.hasOwnProperty.call(
          this.selectedNavItem,
          "customRoute"
        )
      ) {
        this.selectedNavItem.customRoute = route;
      } else {
        this.$set(this.selectedNavItem, "customRoute", route);
      }
    },

    setSelectedProductRouteEnabled(isEnabled) {
      if (isEnabled) {
        const productRoute = this.getProductRoute(
          this.selectedNavItem.customRoute
        );

        if (
          !productRoute.isProductRoute &&
          this.productRoutePreviousCustomRoute === null
        ) {
          this.productRoutePreviousCustomRoute =
            this.selectedNavItem.customRoute || "";
        }

        this.setProductRouteForm(productRoute);
        this.setSelectedCustomRoute(
          this.buildProductRoute(
            this.selectedProductRouteMode,
            this.selectedProductRouteType,
            this.selectedProductRouteCustomType
          )
        );
      } else if (this.isSelectedProductRoute) {
        this.setSelectedCustomRoute(
          this.productRoutePreviousCustomRoute !== null
            ? this.productRoutePreviousCustomRoute
            : ""
        );
        this.productRoutePreviousCustomRoute = null;
        this.setProductRouteForm({
          mode: "masterplan",
          type: "any",
          customType: ""
        });
      }
    },

    setSelectedProductRouteMode(mode) {
      this.productRouteForm.mode = mode;
      if (
        mode === "masterplan" &&
        ["packages", "dwellings"].indexOf(this.selectedProductRouteType) !== -1
      ) {
        this.productRouteForm.type = "any";
      }

      this.setSelectedCustomRoute(
        this.buildProductRoute(
          this.selectedProductRouteMode,
          this.selectedProductRouteType,
          this.selectedProductRouteCustomType
        )
      );
    },

    setSelectedProductRouteType(type) {
      this.productRouteForm.type = type;
      this.setSelectedCustomRoute(
        this.buildProductRoute(
          this.selectedProductRouteMode,
          this.selectedProductRouteType,
          this.selectedProductRouteCustomType
        )
      );
    },

    setSelectedProductRouteCustomType(customType) {
      this.productRouteForm.customType = customType;
      this.setSelectedCustomRoute(
        this.buildProductRoute(
          this.selectedProductRouteMode,
          this.selectedProductRouteType,
          this.selectedProductRouteCustomType
        )
      );
    },

    navItemClick(event, item) {
      // get clicked element position
      const clickedElement = event.target;
      const height = clickedElement.getBoundingClientRect().height;
      const width = clickedElement.getBoundingClientRect().width;

      let modalLeft = clickedElement.offsetLeft - this.modalWidth + width / 2;
      let modalTop = clickedElement.offsetTop - 15;

      this.modalX = modalLeft;
      this.modalY = modalTop;
      this.selectedNavItem = item;
      this.productRoutePreviousCustomRoute = null;
      this.setProductRouteForm(this.getProductRoute(item.customRoute));
    },

    removeSelectedItem() {
      const selectedItemId = this.selectedNavItem.id;

      // remove base item
      _.remove(this.selectedCategory.children, function(baseItem) {
        if (baseItem.id === selectedItemId) {
          return baseItem;
        } else {
          // or child item
          _.remove(baseItem.children, function(childItem) {
            if (childItem.id === selectedItemId) {
              return childItem;
            }
          });
        }
      });

      this.closeEditBox();
    },

    removeCategory() {
      // show alert
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!"
      }).then(result => {
        if (result.value) {
          Swal.fire("Deleted!", "The screen has been deleted.", "success");

          const selectedTabId = this.selectedTabId;

          this.navigation.categories = _.remove(
            this.navigation.categories,
            function(screen) {
              return screen.id !== selectedTabId;
            }
          );

          this.selectedTabId = this.navigation.categories[0].id;
        }
      });
    },

    updateNavigation() {
      this.$store
        .dispatch(NAVIGATION_EDIT, this.navigation.categories)
        .then(res => {
          this.initialPageJSON = JSON.stringify(this.navigation);
          this.$notify({
            message: "Update successful",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "success"
          });
        })
        .catch(err => {
          this.$notify({
            message: "Update failed - " + res,
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "danger"
          });
        });
    },

    setSelectedTabId(tabId) {
      this.selectedTabId = tabId;
    },

    setBaseItemsOrder() {
      this.selectedCategory.children.forEach((baseItem, index) => {
        baseItem.order = index;
      });
    },

    setChildItemsOrder(baseItem) {
      baseItem.children.forEach((childItem, index) => {
        childItem.order = index;
      });
    }
  }
};
</script>
<style lang="scss" scoped>
.card-wrapper {
  padding-right: 15px;
  padding-left: 15px;
  width: 100%;
}

.btn-tab {
  background-color: #f0f0f0;
  margin: 0 1px;
}

.tab-icon {
  margin-left: 8px;
  opacity: 0.4;
}

.modal-edit-box {
  label {
    color: #fff;
  }
}
</style>
